import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  CreateInvoiceInput,
  CreateInvoiceLineInput,
  Invoice,
  InvoiceDocumentType,
  UpdateInvoiceInput,
} from '@catalyst-psa/shared';
import { INVOICE_DOCUMENT_TYPES, INVOICE_DOCUMENT_TYPE_LABELS } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Button } from '../../components/ui/Button.js';
import { Modal } from '../../components/ui/Modal.js';
import { FormActions, useSaveMode } from '../../components/ui/FormActions.js';
import { SelectField, TextAreaField, TextField } from '../../components/ui/Field.js';
import { CustomerSelectField } from '../customers/CustomerSelectField.js';
import { useAgreements } from '../agreements/api.js';
import { useProjects } from '../projects/api.js';
import { useProductOptions } from '../products/api.js';
import { ProductPicker } from '../products/ProductPicker.js';
import { useCreateInvoice, useUpdateInvoice } from './api.js';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

interface LineState {
  productId: string;
  description: string;
  quantity: string;
  unitRate: string;
}

const EMPTY_LINE: LineState = { productId: '', description: '', quantity: '1', unitRate: '0' };

interface InvoiceFormModalProps {
  isOpen: boolean;
  /** When set, edit this manual draft; otherwise create a new document. */
  invoice: Invoice | null;
  /** Document type when creating (ignored when editing). */
  documentType: InvoiceDocumentType;
  onClose: () => void;
}

function lineAmount(line: LineState): number {
  const qty = Number(line.quantity);
  const rate = Number(line.unitRate);
  if (!Number.isFinite(qty) || !Number.isFinite(rate)) return 0;
  return qty * rate;
}

export function InvoiceFormModal({
  isOpen,
  invoice: invoiceProp,
  documentType,
  onClose,
}: InvoiceFormModalProps): JSX.Element {
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<Invoice | null>(invoiceProp);
  const isEdit = invoice !== null;
  const effectiveType: InvoiceDocumentType = isEdit ? invoice.documentType : documentType;
  const saveMode = useSaveMode();

  const [customerId, setCustomerId] = useState('');
  const [agreementId, setAgreementId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [notes, setNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [lines, setLines] = useState<LineState[]>([EMPTY_LINE]);

  const createMutation = useCreateInvoice();
  const updateMutation = useUpdateInvoice();
  const mutation = isEdit ? updateMutation : createMutation;

  const productsQuery = useProductOptions();
  const products = productsQuery.data ?? [];

  // Agreements/projects are scoped to the chosen customer for the attachment dropdowns.
  const agreementsQuery = useAgreements({
    page: 1,
    pageSize: 200,
    customerId: customerId || undefined,
  });
  const projectsQuery = useProjects({
    page: 1,
    pageSize: 200,
    customerId: customerId || undefined,
  });
  const agreements = customerId ? (agreementsQuery.data?.data ?? []) : [];
  const projects = customerId ? (projectsQuery.data?.data ?? []) : [];

  // Sync the incoming prop into local state whenever the modal (re)opens.
  useEffect(() => {
    if (isOpen) setInvoice(invoiceProp);
  }, [isOpen, invoiceProp]);

  // Reset the form whenever the modal (re)opens or switches records.
  useEffect(() => {
    if (!isOpen) return;
    createMutation.reset();
    updateMutation.reset();
    saveMode.reset();
    if (invoice) {
      setCustomerId(invoice.customerId);
      setAgreementId(invoice.agreementId ?? '');
      setProjectId(invoice.projectId ?? '');
      setInvoiceDate(invoice.issueDate ?? '');
      setNotes(invoice.notes ?? '');
      setInternalNotes(invoice.internalNotes ?? '');
      setLines(
        (invoice.lines ?? []).length > 0
          ? (invoice.lines ?? []).map((l) => ({
              productId: l.productId ?? '',
              description: l.description,
              quantity: l.quantity,
              unitRate: l.unitRate,
            }))
          : [EMPTY_LINE],
      );
    } else {
      setCustomerId('');
      setAgreementId('');
      setProjectId('');
      setInvoiceDate('');
      setNotes('');
      setInternalNotes('');
      setLines([EMPTY_LINE]);
    }
  }, [isOpen, invoice?.id]);

  const total = useMemo(() => lines.reduce((sum, l) => sum + lineAmount(l), 0), [lines]);

  const agreementOptions = useMemo(
    () => [
      { value: '', label: 'None' },
      ...agreements.map((a) => ({ value: a.id, label: `#${a.number} — ${a.name}` })),
    ],
    [agreements],
  );

  const projectOptions = useMemo(
    () => [
      { value: '', label: 'None' },
      ...projects.map((p) => ({ value: p.id, label: `#${p.number} — ${p.name}` })),
    ],
    [projects],
  );

  const typeOptions = INVOICE_DOCUMENT_TYPES.map((value) => ({
    value,
    label: INVOICE_DOCUMENT_TYPE_LABELS[value],
  }));

  const setLine = (index: number, patch: Partial<LineState>): void => {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  };

  const handleProductChange = (index: number, productId: string): void => {
    const product = products.find((p) => p.id === productId);
    setLine(index, {
      productId,
      ...(product ? { description: product.name, unitRate: product.unitPrice } : {}),
    });
  };

  const addLine = (): void => setLines((prev) => [...prev, { ...EMPTY_LINE }]);
  const removeLine = (index: number): void =>
    setLines((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));

  const cleanedLines = (): CreateInvoiceLineInput[] =>
    lines
      .filter((l) => l.description.trim().length > 0)
      .map((l) => ({
        productId: l.productId ? l.productId : null,
        description: l.description.trim(),
        quantity: l.quantity.trim() || '0',
        unitRate: l.unitRate.trim() || '0',
      }));

  const handleSubmit = (): void => {
    const payloadLines = cleanedLines();
    if (isEdit) {
      const input: UpdateInvoiceInput = {
        agreementId: agreementId || null,
        projectId: projectId || null,
        notes: notes.trim() || null,
        internalNotes: internalNotes.trim() || null,
        lines: payloadLines,
      };
      updateMutation.mutate(
        { id: invoice.id, input },
        { onSuccess: () => saveMode.closeAfterSaveRef.current && onClose() },
      );
    } else {
      const input: CreateInvoiceInput = {
        documentType: effectiveType,
        customerId,
        agreementId: agreementId || null,
        projectId: projectId || null,
        invoiceDate: invoiceDate || null,
        notes: notes.trim() || null,
        internalNotes: internalNotes.trim() || null,
        lines: payloadLines,
      };
      createMutation.mutate(input, {
        onSuccess: (created) => {
          if (saveMode.closeAfterSaveRef.current) {
            onClose();
          } else {
            setInvoice(created);
          }
        },
      });
    }
  };

  const goToCustomer = (): void => {
    const targetCustomerId = customerId || invoice?.customerId;
    if (!targetCustomerId) {
      return;
    }
    navigate(`/customers?returnCustomerId=${targetCustomerId}&returnTab=invoices`);
  };

  const error =
    mutation.error instanceof ApiError
      ? mutation.error.message
      : mutation.error
        ? 'Failed to save the document.'
        : null;

  const typeLabel = INVOICE_DOCUMENT_TYPE_LABELS[effectiveType];
  const canSubmit = (isEdit || Boolean(customerId)) && cleanedLines().length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="wide"
      title={isEdit ? `Edit ${invoice.number}` : `New ${typeLabel.toLowerCase()}`}
      headerActions={
        <Button
          variant="secondary"
          size="sm"
          onClick={goToCustomer}
          disabled={!customerId && !invoice?.customerId}
        >
          Go to Customer
        </Button>
      }
      footer={
        <FormActions
          onSubmit={handleSubmit}
          saveMode={saveMode}
          isPending={mutation.isPending}
          disabled={!canSubmit}
          onCancel={onClose}
          leading={
            <p className="mr-auto text-sm font-medium text-slate-700">
              Total: {currency.format(total)}
              {effectiveType === 'credit_memo' ? ' credit' : ''}
            </p>
          }
        />
      }
    >
      <div className="space-y-4">
        {error ? (
          <p className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          {isEdit ? (
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Document</p>
              <p className="mt-1 text-sm font-medium text-slate-900">
                {typeLabel} · {invoice.customerName ?? '—'}
              </p>
            </div>
          ) : (
            <>
              <SelectField
                label="Type"
                value={effectiveType}
                disabled
                options={typeOptions}
                onChange={() => undefined}
              />
              <CustomerSelectField
                label="Customer"
                value={customerId}
                onChange={(value) => {
                  setCustomerId(value);
                  setAgreementId('');
                  setProjectId('');
                }}
                required
              />
            </>
          )}

          <SelectField
            label="Attach to agreement"
            value={agreementId}
            onChange={(e) => setAgreementId(e.target.value)}
            disabled={!customerId}
            options={agreementOptions}
          />

          <SelectField
            label="Attach to project"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            disabled={!customerId}
            options={projectOptions}
          />

          {!isEdit ? (
            <TextField
              label="Invoice date"
              type="date"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
            />
          ) : null}
        </div>

        {/* Line items */}
        <div className="rounded-lg border border-slate-200">
          <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2">
            <span className="text-sm font-medium text-slate-700">Line items</span>
            <Button variant="secondary" onClick={addLine} className="px-2.5 py-1 text-xs">
              Add line
            </Button>
          </div>
          <div className="divide-y divide-slate-100">
            {lines.map((line, index) => (
              <div key={index} className="grid gap-2 p-3 sm:grid-cols-12 sm:items-end">
                <div className="sm:col-span-3">
                  <ProductPicker
                    label="Product"
                    products={products}
                    value={line.productId}
                    emptyLabel="No product (free-form)"
                    onChange={(productId) => handleProductChange(index, productId)}
                  />
                </div>
                <div className="sm:col-span-4">
                  <TextField
                    label="Description"
                    value={line.description}
                    onChange={(e) => setLine(index, { description: e.target.value })}
                  />
                </div>
                <div className="sm:col-span-1">
                  <TextField
                    label="Qty"
                    type="number"
                    step="0.01"
                    value={line.quantity}
                    onChange={(e) => setLine(index, { quantity: e.target.value })}
                  />
                </div>
                <div className="sm:col-span-2">
                  <TextField
                    label="Rate"
                    type="number"
                    step="0.01"
                    value={line.unitRate}
                    onChange={(e) => setLine(index, { unitRate: e.target.value })}
                  />
                </div>
                <div className="flex items-center justify-between gap-2 sm:col-span-2">
                  <span className="text-sm tabular-nums text-slate-700">
                    {currency.format(lineAmount(line))}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeLine(index)}
                    disabled={lines.length === 1}
                    className="text-xs text-rose-600 hover:underline disabled:opacity-40"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <TextAreaField
            label="Customer note"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Shown on the invoice document"
          />
          <TextAreaField
            label="Internal note"
            rows={3}
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
            placeholder="Staff-only, never shown to the customer"
          />
        </div>
      </div>
    </Modal>
  );
}
